import React, {useState, useRef, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {Box, ButtonBase, Collapse, Divider, Typography} from '@mui/material';
import {ChevronRight, Clock, FileText, Loader, PencilLine} from 'lucide-react';
import {GREY_30, PENNY_THINKING} from '../../theme';

const SCROLL_THRESHOLD = 20;
const ACTIVITY_ICON_SIZE = 12;

const TOOL_COMPLETE_ICONS = {
  tiptapRead: FileText,
  tiptapReadSelection: FileText,
  tiptapEdit: PencilLine,
};

const ThinkingSpinner = () => (
  <Loader
    size={ACTIVITY_ICON_SIZE}
    color={PENNY_THINKING}
    className="thinking-spinner"
    style={{display: 'block'}}
  />
);

/**
 * Completed or in-progress tool activity row.
 */
export const ToolActivityRow = ({
  toolName,
  activeLabel,
  completeLabel,
  isComplete,
}) => {
  const CompleteIcon = TOOL_COMPLETE_ICONS[toolName] || ChevronRight;
  const label = isComplete ? completeLabel : activeLabel;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px',
        my: 1,
      }}
    >
      {isComplete ? (
        <CompleteIcon
          size={ACTIVITY_ICON_SIZE}
          color={PENNY_THINKING}
          style={{display: 'block'}}
        />
      ) : (
        <ThinkingSpinner />
      )}
      <Typography
        variant="bodySmall"
        sx={{color: PENNY_THINKING, userSelect: 'none'}}
      >
        {label}
      </Typography>
    </Box>
  );
};

ToolActivityRow.propTypes = {
  toolName: PropTypes.string,
  activeLabel: PropTypes.string.isRequired,
  completeLabel: PropTypes.string.isRequired,
  isComplete: PropTypes.bool,
};

/**
 * Standalone thinking indicator — spinner + label, no expand/collapse.
 * Used for the initial loading state before any activity entries exist.
 */
export const ThinkingIndicator = ({label = 'Thinking...'}) => (
  <Box sx={{display: 'flex', alignItems: 'center', gap: '6px', padding: '4px'}}>
    <ThinkingSpinner />
    <Typography
      variant="bodySmall"
      sx={{color: PENNY_THINKING, userSelect: 'none'}}
    >
      {label}
    </Typography>
  </Box>
);

ThinkingIndicator.propTypes = {
  label: PropTypes.string,
};

const PREFIX_TEXT = 'Thought for';
export const formatThinkingDuration = (seconds) => {
  if (!seconds || seconds < 1) return `${PREFIX_TEXT} a moment.`;
  if (seconds < 60) {
    return `${PREFIX_TEXT} ${seconds} second${seconds === 1 ? '' : 's'}.`;
  }
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  const minLabel = `${minutes} minute${minutes === 1 ? '' : 's'}`;
  if (remaining === 0) {
    return `${PREFIX_TEXT} ${minLabel}.`;
  }
  return `${PREFIX_TEXT} ${minLabel} and ${remaining} second${remaining === 1 ? '' : 's'}.`;
};

/**
 * ThinkingBlock — represents a single thinking phase.
 * Each phase has its own block of content and duration.
 * isStreaming=true means this phase is still accumulating tokens.
 */
const ThinkingBlock = ({
  thinkingBlocks,
  isStreaming,
  thinkingDurationMs,
  defaultExpanded = false,
  onExpandedChange,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // If defaultExpanded=true, treat as already user-toggled to prevent auto-collapse on mount
  const [userToggled, setUserToggled] = useState(defaultExpanded);
  const contentRef = useRef(null);
  const userScrolledUp = useRef(false);
  const scrollRafRef = useRef(null);
  // Ref guard to prevent stale-closure re-fires during rapid thinking_delta chunks
  const autoExpandedRef = useRef(defaultExpanded);

  useEffect(() => {
    if (userToggled || !isStreaming || autoExpandedRef.current) return;
    if (thinkingBlocks?.some((b) => b.content)) {
      autoExpandedRef.current = true;
      setExpanded(true);
      onExpandedChange?.(true);
    }
  }, [thinkingBlocks, isStreaming, userToggled, onExpandedChange]);

  useEffect(() => {
    if (!isStreaming && !userToggled) {
      autoExpandedRef.current = false;
      setExpanded(false);
      onExpandedChange?.(false);
    }
  }, [isStreaming, userToggled, onExpandedChange]);

  const handleToggle = useCallback(() => {
    setUserToggled(true);
    setExpanded((prev) => {
      const next = !prev;
      onExpandedChange?.(next);
      return next;
    });
  }, [onExpandedChange]);

  const handleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledUp.current = distanceFromBottom > SCROLL_THRESHOLD;
  }, []);

  useEffect(() => {
    if (!expanded) {
      // Reset scroll position tracking when collapsed so next expand auto-scrolls again
      userScrolledUp.current = false;
      return;
    }
    const el = contentRef.current;
    if (!el || userScrolledUp.current) return;
    // Debounce via rAF to avoid layout thrash on every streaming token
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const container = contentRef.current;
      if (container && !userScrolledUp.current) {
        container.scrollTop = container.scrollHeight;
      }
    });
    return () => {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [thinkingBlocks, expanded]);

  const hasThinkingContent = thinkingBlocks?.some((b) => b.content);
  const allComplete =
    thinkingBlocks?.length > 0 && thinkingBlocks.every((b) => b.isComplete);
  const durationSeconds = thinkingDurationMs
    ? Math.round(thinkingDurationMs / 1000)
    : null;

  const showSpinner = !allComplete || isStreaming;
  const label =
    allComplete && !isStreaming
      ? formatThinkingDuration(durationSeconds)
      : 'Thinking...';

  return (
    <Box sx={{mb: 1}}>
      <ButtonBase
        className="thinking-block-header"
        onClick={hasThinkingContent ? handleToggle : undefined}
        aria-expanded={expanded}
        disableRipple
        sx={{cursor: hasThinkingContent ? 'pointer' : 'default'}}
      >
        {!showSpinner ? (
          hasThinkingContent ? (
            <ChevronRight
              size={ACTIVITY_ICON_SIZE}
              color={PENNY_THINKING}
              style={{
                display: 'block',
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          ) : (
            <Clock
              size={ACTIVITY_ICON_SIZE}
              color={PENNY_THINKING}
              style={{display: 'block'}}
            />
          )
        ) : (
          <ThinkingSpinner />
        )}
        <Typography
          variant="bodySmall"
          sx={{color: PENNY_THINKING, userSelect: 'none'}}
        >
          {label}
        </Typography>
      </ButtonBase>
      {hasThinkingContent && (
        <Collapse in={expanded}>
          <Box
            ref={contentRef}
            className="thinking-block-content"
            onScroll={handleScroll}
          >
            {thinkingBlocks.map((block, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <Divider
                    sx={{my: 1, borderColor: 'rgba(54, 129, 122, 0.2)'}}
                  />
                )}
                <Typography
                  variant="bodySmall"
                  sx={{whiteSpace: 'pre-wrap', color: GREY_30}}
                >
                  {block.content}
                </Typography>
              </React.Fragment>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

ThinkingBlock.propTypes = {
  thinkingBlocks: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string.isRequired,
      isComplete: PropTypes.bool.isRequired,
    })
  ),
  isStreaming: PropTypes.bool,
  thinkingDurationMs: PropTypes.number,
  defaultExpanded: PropTypes.bool,
  onExpandedChange: PropTypes.func,
};

export default ThinkingBlock;
